import axios, { AxiosRequestConfig } from 'axios'
import CryptoJS from 'crypto-js'
import { createNotification } from 'factories/notificationFactory'
import { endRequest, startRequest } from 'reducers/stateReducer'
import { userUnloaded, userModuleAccessCleared } from 'reducers/userReducer'
import { Dictionary } from 'types'
import store, { getAccessToken, getLocale, getTeamFilter } from 'utils/configureStore'
import userManager from 'utils/userManager'

interface RequestConfig extends AxiosRequestConfig {
  queryParameters?: object
  mode?: string
  returnResponse?: boolean
  silent?: boolean
}

interface requestBuildingSettings {
  base: string
  cullIdsInPostRequests?: boolean
}

const largeFileChecksumCutoff = 100 * 1024 * 1024

let _logoutInProgress = false
const triggerLogout = () => {
  if (_logoutInProgress) return
  if (getAccessToken()) return // token was just silently renewed — skip logout
  _logoutInProgress = true
  store.dispatch(userUnloaded())
  store.dispatch(userModuleAccessCleared())
  userManager.removeUser().then(() => {
    window.location.replace('/')
  })
}

export const requestBuilder = (settings: requestBuildingSettings) => async <T = any>(url: string, options: RequestConfig = {}, customHeaders: Dictionary<string> = {}): Promise<T> => {
  const defaultOptions = (): RequestConfig => ({
    method: 'GET',
    responseType: 'json',
    queryParameters: {},
    mode: 'cors',
    returnResponse: false,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    validateStatus: (s) => s >= 200 && s < 300,
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAccessToken()}`,
      'OData-Version': '4.0',
      'OData-MaxVersion': '4.0',
      'ZUMO-API-VERSION': '2.0.0',
      Prefer: 'return=representation',
    },
  })

  return new Promise(async (resolve, reject) => {
    const params: RequestConfig = Object.assign({}, defaultOptions(), options)
    const isBinaryUpload = params.data instanceof FormData || params.data instanceof File

    if (params.data instanceof FormData) {
      for (let value of params.data.values()) {
        if (value instanceof File) {
          if (shouldIncludeChecksum(value)) {
            params.headers['FileMD5Hash'] = await getChecksum(value)
          } else {
            delete params.headers['FileMD5Hash']
          }
        }
      }
      // Remove Content-Type header for FormData to let browser set correct multipart boundary
      delete params.headers['Content-Type']
    } else if (params.data instanceof File) {
      // Handle direct File upload - send as binary stream
      const file = params.data
      params.data = file  // Send File directly, not as FormData
      if (shouldIncludeChecksum(file)) {
        params.headers['FileMD5Hash'] = await getChecksum(file)
      } else {
        delete params.headers['FileMD5Hash']
      }
      params.headers['Content-Type'] = 'application/octet-stream'  // Set correct content type for binary file
    }

    if (['post', 'put', 'patch'].includes(params.method?.toLowerCase() ?? '') && typeof params.data === 'object' && false === params.data instanceof FormData) {
      if (settings.cullIdsInPostRequests && params.method?.toLowerCase() === 'post') {
        //some odata apis will fail if inserting objects with an Id field
        delete params.data.Id
      }
      // remove the class name folder
      if (undefined !== params.data._className) {
        delete params.data._className
      }
      // remove any other properties we want to
      if (undefined !== params.data._stripProperties) {
        for (var i in params.data._stripProperties) {
          var paramName = params.data._stripProperties[i]
          delete params.data[paramName]
        }
        delete params.data._stripProperties
      }
    }

    const teamFilterId = getTeamFilter()

    if (teamFilterId !== '-1') {
      params.headers['X-WORKAWARE-TEAMFILTER'] = teamFilterId
    }

    const locale = getLocale()

    if (locale) {
      params.headers['X-WORKAWARE-LOCALE'] = locale
    }

    if (customHeaders) {
      for (let prop in customHeaders) {
        var v = customHeaders[prop]
        if ((v === undefined || v === 'false') && params.headers[prop]) {
          delete params.headers[prop]
        } else if (v !== undefined && v !== 'false') {  // Only set header if value is not undefined
          params.headers[prop] = v
        }
      }
    }

    store.dispatch(startRequest())

    if (!url.startsWith('http')) {
      url = settings.base + url
    }

    if (isBinaryUpload) {
      sendBinaryRequest<T>(url, params)
        .then((result) => {
          store.dispatch(endRequest())
          resolve(result)
        })
        .catch((error: { message: string; response?: any }) => {
          store.dispatch(endRequest())

          if (error?.response?.status === 401) {
            triggerLogout()
            reject(error)
            return
          }

          if (error?.response?.status === 404) {
            reject(error)
            return
          }

          if (error?.response?.status === 413) {
            if (!params.silent) createNotification.errorKey('error.fileSizeLimit', 'File size exceeds the allowed limit.')
            reject(error)
            return
          }

          if (shouldSuppressToast(error)) {
            console.error('[request]', error?.response?.status, error.message)
            reject(error)
            return
          }

          const message = error?.response?.data?.error?.message || error.message
          if (!params.silent) createNotification.error(message)
          reject(error)
        })
      return
    }

    axios(url, params)
      .then((result) => {
        store.dispatch(endRequest())
        if (params.returnResponse) {
          resolve(result as any)
        } else {
          resolve(result.data)
        }
      })
      .catch((error: { message: string; response?: any }) => {
        store.dispatch(endRequest())

        if (error?.response?.status === 401) {
          triggerLogout()
          reject(error)
          return
        }

        if (error?.response?.status === 404) {
          reject(error)
          return
        }

        if (error?.response?.status === 413) {
          if (!params.silent) createNotification.errorKey('error.fileSizeLimit', 'File size exceeds the allowed limit.')
          reject(error)
          return
        }

        if (shouldSuppressToast(error)) {
          console.error('[request]', error?.response?.status, error.message)
          reject(error)
          return
        }

        const data = error?.response?.data
        const message =
          data?.error?.message?.value ||
          data?.error?.message ||
          data?.message ||
          data?.value ||
          error.message
        if (!params.silent) createNotification.error(message)
        reject(error)
      })
  })
}

function shouldSuppressToast(error: { message: string; response?: any }): boolean {
  const status = error?.response?.status
  if (status === 409 || status === 500 || status === 403 || status === 503) return true
  const msg = (error.message || '').toLowerCase()
  return msg.includes('network error') || msg.includes('service unavailable') || msg.includes('internal server error')
}

const checksumChunkSize = 8 * 1024 * 1024

const shouldIncludeChecksum = (file: Blob): boolean => file.size <= largeFileChecksumCutoff

const sendBinaryRequest = async <T = any>(url: string, params: RequestConfig): Promise<T> => {
  const response = await fetch(url, {
    method: params.method,
    body: params.data as BodyInit,
    headers: params.headers as Record<string, string>,
  })

  if (!(params.validateStatus?.(response.status) ?? response.ok)) {
    let errorData: any

    try {
      errorData = await response.clone().json()
    } catch {
      try {
        errorData = await response.text()
      } catch {
        errorData = undefined
      }
    }

    throw {
      message: response.statusText || `Request failed with status code ${response.status}`,
      response: {
        status: response.status,
        data: errorData,
      },
    }
  }

  if (params.returnResponse) {
    return (response as unknown) as T
  }

  if (params.responseType === 'blob') {
    return (await response.blob()) as unknown as T
  }

  if (params.responseType === 'text') {
    return (await response.text()) as unknown as T
  }

  // Handle 204 No Content responses
  if (response.status === 204) {
    return {} as T
  }

  const text = await response.text()
  if (!text.trim()) return {} as T
  try {
    return JSON.parse(text) as T
  } catch {
    return text as unknown as T
  }
}


const getChecksum = (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const hash = CryptoJS.algo.MD5.create()
    const reader = new FileReader()
    let offset = 0

    const readNextChunk = () => {
      const chunk = file.slice(offset, offset + checksumChunkSize)
      reader.readAsArrayBuffer(chunk)
    }

    reader.onload = (e) => {
      const result = e?.target?.result

      if (result instanceof ArrayBuffer) {
        hash.update(CryptoJS.lib.WordArray.create(result as any))
        offset += result.byteLength

        if (offset < file.size) {
          readNextChunk()
          return
        }

        resolve(hash.finalize().toString())
      } else {
        reject(new Error('Unable to read file chunk for checksum calculation.'))
      }
    }

    reader.onerror = () => reject(reader.error || new Error('Unable to calculate file checksum.'))
    reader.onabort = () => reject(new Error('File checksum calculation was aborted.'))

    readNextChunk()
  })
}
